/**
 * BookHero — identity block: cover, title, authors, community rating,
 * publish metadata, subject/genre pills. Same cover-left/info-right shape
 * BookDetailModal used, generalized to any resolved book (shelved, seed
 * catalog, or live Open Library).
 */

import React from 'react';
import { Stars } from '@/components/site/primitives';
import BookCoverArt from '@/components/books/BookCoverArt';

export default function BookHero({ book, ratingSummary }) {
  const meta = [
    book.publishYear ? `First published ${book.publishYear}` : null,
    book.publisher,
  ].filter(Boolean).join(' · ');

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:gap-8">
      <BookCoverArt book={book} className="h-64 w-44 shrink-0 shadow-lift" />

      <div className="min-w-0 flex-1 text-center sm:text-left">
        <h1 className="font-display text-3xl font-semibold leading-tight text-ink text-balance sm:text-4xl">
          {book.title}
        </h1>
        {book.authors?.length > 0 && (
          <p className="mt-1.5 text-base text-subtle">{book.authors.join(', ')}</p>
        )}

        {ratingSummary && (
          <div className="mt-3 flex items-center justify-center gap-2 sm:justify-start">
            <Stars value={Math.round(ratingSummary.average)} size={16} />
            <span className="text-sm font-semibold text-ink">{ratingSummary.average.toFixed(2)}</span>
            <span className="text-sm text-subtle">({ratingSummary.count.toLocaleString()} ratings)</span>
          </div>
        )}

        {meta && <p className="mt-2 text-xs text-subtle">{meta}</p>}

        {book.subjects?.length > 0 && (
          <div className="mt-3 flex flex-wrap justify-center gap-1.5 sm:justify-start">
            {book.subjects.slice(0, 6).map((s) => (
              <span key={s} className="rounded-full bg-cream px-2.5 py-0.5 text-[11px] font-medium text-subtle">
                {s}
              </span>
            ))}
          </div>
        )}

        {(book.isbn13?.[0] || book.isbn10?.[0]) && (
          <p className="mt-3 text-[11px] text-subtle">ISBN {book.isbn13?.[0] ?? book.isbn10?.[0]}</p>
        )}
      </div>
    </div>
  );
}
